import { getItemsByIndustry } from "../data/categoryData";

/**
 * Optional explicit overrides when normalization is not enough (legacy URLs, renames).
 * Values must match static `slug` in categoryData for that industry.
 */
const TOPIC_SLUG_OVERRIDES_BY_INDUSTRY = {
  legal: {
    // Example: "legacy-land-hub": "land-property-law",
  },
  "betting-gaming": {
    // Verbose / marketing URL → canonical menu slug (rich hub + links)
    "sports-betting-tips-and-predictions": "sports-betting-tips",
    // If "-and-" was stripped elsewhere
    "sports-betting-tips-predictions": "sports-betting-tips",
    // Casino hub alternates → `categoryData` slug
    "online-casino-games": "casino-games",
    "online-casino": "casino-games",
    "casino-games-online": "casino-games",
    "ghana-online-casino": "casino-games",
    "casino": "casino-games",
    "best-betting-sites": "best-betting-platforms",
    "top-betting-platforms": "best-betting-platforms",
    "betting-platforms-ghana": "best-betting-platforms",
    "virtual-sports": "virtual-games",
    "virtual-sports-betting": "virtual-games",
    "instant-virtual-betting": "virtual-games",
  },
};

/**
 * Optional: `{ [industryId]: { [canonicalTopicSlug]: { [aliasArticleSlug]: canonicalArticleSlug } } }`
 * Example: `legal: { "land-property-law": { "old-slug": "land-purchase-verification-1" } }`
 */
const ARTICLE_SLUG_ALIASES_BY_INDUSTRY_AND_TOPIC = {};

/**
 * Stable comparison key for topic (and article) URL segments: tolerates CMS/backends
 * inserting “and” between terms (`marriage-and-family-law` ↔ `marriage-family-law`),
 * underscores, casing, and repeated hyphens.
 *
 * @param {string} [slug]
 * @returns {string}
 */
export function normalizeSlugForTopicMatch(slug) {
  if (slug == null || slug === "") return "";
  let s = String(slug).toLowerCase().trim().replace(/_/g, "-");
  s = s.replace(/-+/g, "-").replace(/^-+|-+$/g, "");
  while (s.includes("-and-")) {
    s = s.replace(/-and-/g, "-").replace(/-+/g, "-").replace(/^-+|-+$/g, "");
  }
  return s;
}

/** @param {string} [a] @param {string} [b] @returns {boolean} */
export function topicSlugsMatch(a, b) {
  const na = normalizeSlugForTopicMatch(a);
  const nb = normalizeSlugForTopicMatch(b);
  return na !== "" && na === nb;
}

/**
 * Decode route param and resolve to the canonical `slug` in categoryData when possible
 * (so registry + mockContent stay aligned with the static menu).
 *
 * @param {string} [industryId]
 * @param {string} [rawSlug]
 * @returns {string} canonical slug for lookups, or decoded segment if unknown
 */
export function resolveCanonicalTopicSlug(industryId, rawSlug) {
  if (rawSlug == null || rawSlug === "") return "";
  let decoded;
  try {
    decoded = decodeURIComponent(String(rawSlug)).trim();
  } catch {
    decoded = String(rawSlug).trim();
  }
  if (!decoded) return "";

  const overrides = industryId ? TOPIC_SLUG_OVERRIDES_BY_INDUSTRY[industryId] : null;
  if (overrides) {
    const normalizedKey = normalizeSlugForTopicMatch(decoded);
    const fromOverride =
      overrides[decoded] ?? overrides[decoded.toLowerCase()] ?? overrides[normalizedKey];
    if (fromOverride) return fromOverride;
  }

  const items = industryId ? getItemsByIndustry(industryId) : [];
  if (items.length > 0) {
    const want = normalizeSlugForTopicMatch(decoded);
    if (want) {
      const hit = items.find((item) => normalizeSlugForTopicMatch(item.slug) === want);
      if (hit) return hit.slug;
    }
  }

  return decoded;
}

/**
 * @param {string} [industryId]
 * @param {string} [rawTopicSlug]
 * @param {string} [rawArticleSlug]
 * @returns {string} decoded article slug for lookups (aliases applied before fuzzy match in mockContent)
 */
export function resolveCanonicalArticleSlug(industryId, rawTopicSlug, rawArticleSlug) {
  if (rawArticleSlug == null || rawArticleSlug === "") return "";
  let decoded;
  try {
    decoded = decodeURIComponent(String(rawArticleSlug)).trim();
  } catch {
    decoded = String(rawArticleSlug).trim();
  }
  if (!decoded) return "";
  const topicKey = resolveCanonicalTopicSlug(industryId, rawTopicSlug);
  const topicMap =
    industryId && topicKey
      ? ARTICLE_SLUG_ALIASES_BY_INDUSTRY_AND_TOPIC[industryId]?.[topicKey]
      : null;
  if (topicMap && topicMap[decoded]) return topicMap[decoded];
  return decoded;
}
